import { useEffect, type ReactNode } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { recoveryRedirectTarget } from '../lib/authRecovery'

export const AUTH_CALLBACK_ERROR = 'authCallbackError' as const

function decodeAuthMessage (raw: string) {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '))
  } catch {
    return raw
  }
}

/**
 * Normalizes Supabase auth redirects: recovery tokens may land on Site URL (/).
 * Sends recovery hash to /reset-password synchronously so route gates cannot drop the tokens.
 */
export function AuthUrlBootstrap ({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const recoveryTo = recoveryRedirectTarget(
    location.pathname,
    typeof window !== 'undefined' ? window.location.hash : location.hash,
    location.search
  )

  useEffect(() => {
    const hash = window.location.hash
    const search = location.search

    if (search) {
      const q = new URLSearchParams(search)
      const err = q.get('error')
      if (err) {
        const desc = q.get('error_description')
        const msg = desc ? decodeAuthMessage(desc) : decodeAuthMessage(err)
        navigate(
          { pathname: '/login', search: '' },
          { replace: true, state: { [AUTH_CALLBACK_ERROR]: msg } }
        )
        return
      }
    }

    if (!hash || hash.length < 2) return

    const params = new URLSearchParams(hash.slice(1))
    const err = params.get('error')

    if (err) {
      const desc = params.get('error_description')
      const msg = desc ? decodeAuthMessage(desc) : decodeAuthMessage(err)
      navigate('/login', { replace: true, state: { [AUTH_CALLBACK_ERROR]: msg } })
    }
  }, [location.pathname, location.search, navigate])

  if (recoveryTo) {
    return <Navigate to={recoveryTo} replace />
  }

  return <>{children}</>
}
